import { CheckCircle2, ChevronDown, Crown, Radio, Trophy } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ProjectLogo } from '@/pages/Judges/components/ProjectLogo';
import type { StageRanking, StageRankingRow } from '@/types';

// The scores come unrounded; they are only rounded here, for display.
function formatScore(score: number | null): string {
  if (score === null) return '–';
  return `${score > 0 ? '+' : ''}${score.toFixed(3)}`;
}

function formatScaled(score: number | null): string {
  return score === null ? '–' : score.toFixed(1);
}

// Each stage's live ranking, one collapsible block per stage (StageRanking). The page
// polls it, so it moves as the judges grade.
export function StageRankings({ rankings }: { rankings: StageRanking[] }) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-1">
        <CardTitle>Rankings by stage</CardTitle>
        <p className="text-muted-foreground text-sm">
          Each judge's grades are normalised against all their own grades on
          the stage (z-score). <strong>Score 0–100</strong>: the judges' average
          z-score per criterion, put on one 0–100 scale for the whole stage
          (min–max), then weighted. <strong>Score (z)</strong>: the weighted
          z-scores averaged over judges. Both always rank the same. Live and
          provisional until every judge has finalised and every ballot is
          full. The final stage's winner wins the event.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {rankings.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground">
            No stages yet.
          </p>
        ) : (
          rankings.map((ranking) => (
            <StageRankingBlock key={ranking.stageId} ranking={ranking} />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function StageRankingBlock({ ranking }: { ranking: StageRanking }) {
  const anyScored = ranking.rows.some((row) => row.score !== null);
  const [open, setOpen] = useState(anyScored);
  // null shows the whole stage; a track name narrows it to that track.
  const [track, setTrack] = useState<string | null>(null);
  const tracks = [...new Set(ranking.rows.map(trackOf))].sort((a, b) =>
    a.localeCompare(b),
  );
  const shown = track
    ? withPlaces(ranking.rows.filter((row) => trackOf(row) === track))
    : ranking.rows.map((row) => ({ row, place: row.rank }));

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 p-3 text-left hover:bg-muted/50">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{ranking.stageName}</span>
          <span className="font-mono text-muted-foreground text-xs">
            /{ranking.stageSlug}
          </span>
          {ranking.isFinal && <Badge>Final</Badge>}
        </span>
        <span className="flex items-center gap-2">
          {ranking.judgeCount === 0 ? (
            <span className="text-muted-foreground text-xs">No judges</span>
          ) : (
            <Badge
              variant={ranking.complete ? 'default' : 'secondary'}
              className="gap-1"
            >
              {ranking.complete ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <Radio className="h-3 w-3" />
              )}
              {ranking.complete ? 'Final' : 'Provisional'} ·{' '}
              {ranking.completedCount}/{ranking.judgeCount} judges finalised
              {ranking.missing > 0 &&
                ` · ${ranking.missing} ballot${ranking.missing === 1 ? '' : 's'} missing`}
            </Badge>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t">
        {ranking.judgeCount === 0 ? (
          <p className="p-4 text-muted-foreground text-sm">
            Assign judges to this stage to get a ranking.
          </p>
        ) : ranking.rows.length === 0 ? (
          <p className="p-4 text-muted-foreground text-sm">
            No projects on this stage.
          </p>
        ) : (
          <>
          {tracks.length > 1 && (
            <div className="flex flex-wrap items-center gap-1 border-b p-2">
              <TrackButton active={track === null} onClick={() => setTrack(null)}>
                All
              </TrackButton>
              {tracks.map((name) => (
                <TrackButton
                  key={name}
                  active={track === name}
                  onClick={() => setTrack(name)}
                >
                  {name}
                </TrackButton>
              ))}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Project</TableHead>
                <TableHead className="border-l text-right">Score 0–100</TableHead>
                {ranking.criteria.map((criterion) => (
                  <TableHead
                    key={criterion.id}
                    className="text-right font-normal text-xs"
                    title={`${criterion.name} (${criterion.weight}%), on the 0–100 scale`}
                  >
                    {criterion.name}
                    <span className="block text-muted-foreground">
                      {criterion.weight}%
                    </span>
                  </TableHead>
                ))}
                <TableHead className="border-l text-right">Score (z)</TableHead>
                <TableHead className="text-right">Avg 0–5</TableHead>
                <TableHead className="text-right">Judges</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map(({ row, place }) => (
                <TableRow
                  key={row.projectId}
                  className={cn(place === 1 && 'bg-primary/5')}
                >
                  <TableCell className="font-bold font-mono">
                    {place === 1 ? (
                      <span className="flex items-center gap-1">
                        {ranking.isFinal && !track ? (
                          <Crown className="h-3.5 w-3.5" />
                        ) : (
                          <Trophy className="h-3.5 w-3.5" />
                        )}
                        1
                      </span>
                    ) : (
                      (place ?? '–')
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <ProjectLogo
                        project={row}
                        className="h-8 w-8 text-[10px]"
                      />
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2 font-medium">
                          {row.name}
                          {ranking.isFinal && row.rank === 1 && (
                            <Badge className="gap-1 text-[10px]">
                              <Crown className="h-3 w-3" />
                              {ranking.complete ? 'Event winner' : 'Leading'}
                            </Badge>
                          )}
                          {track && place === 1 && (
                            <Badge variant="outline" className="gap-1 text-[10px]">
                              <Trophy className="h-3 w-3" />
                              {ranking.complete ? 'Track winner' : 'Leading track'}
                            </Badge>
                          )}
                        </span>
                        <span className="font-mono text-muted-foreground text-xs">
                          {row.trackName ?? 'No track'} · /{row.teamSlug} ·
                          order {row.order}
                        </span>
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="border-l text-right font-bold font-mono tabular-nums">
                    {formatScaled(row.scaledScore)}
                  </TableCell>
                  {row.criterionScores.map((score, index) => (
                    <TableCell
                      key={ranking.criteria[index]?.id ?? index}
                      className="text-right font-mono text-muted-foreground tabular-nums"
                    >
                      {formatScaled(score)}
                    </TableCell>
                  ))}
                  <TableCell className="border-l text-right font-bold font-mono tabular-nums">
                    {formatScore(row.score)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground tabular-nums">
                    {row.rawScore === null ? '–' : row.rawScore.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {row.judgeCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

const NO_TRACK = 'No track';

function trackOf(row: StageRankingRow): string {
  return row.trackName ?? NO_TRACK;
}

// Places within a subset of the stage (one track): the scores stay the stage's — they
// were normalised over the whole stage — and only the places are counted again, so 1
// is the track's winner. Ties share a place; unscored projects have none.
function withPlaces(rows: StageRankingRow[]) {
  return rows.map((row) => ({
    row,
    place:
      row.score === null
        ? null
        : rows.filter(
            (other) => other.score !== null && other.score - row.score! > 1e-9,
          ).length + 1,
  }));
}

function TrackButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={active ? 'default' : 'outline'}
      className="h-7 rounded-none px-2 text-xs"
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}
